import { Component, Vue, Prop } from 'vue-property-decorator';
import { Constants, ProcessConfigurationTypes, StatusReplacement } from '@/Domain/enum';
import _ from 'lodash';
import i18n from '../../../lang';
import { mapGetters, mapState } from 'vuex';
import { ReplacementListState } from '@/Domain/Entities';
import ReplacementService from '@/Services/ReplacementService';
import { ToasterService } from '@/Services/ToasterService';
import { SdSelect } from '@/Components';

@Component({
    name: 'process-config-replacement-states',
    components: {
        SdSelect
    },
    computed: {
        ...mapState('featureFlagsStore', ['multiLanguage']),
        ...mapState('languagesStore', { currentLanguage: 'current' }),
        ...mapGetters('languagesStore', { availableLanguages: 'getTranslatableLanguages' })
    }
})
export default class ProcessConfigReplacementStates extends Vue {
    @Prop() processConfigType!: number;
    @Prop() submittedEdit!: boolean;

    currentLanguage!: string;
    multiLanguage!: boolean;
    availableLanguages!: string[];
    replacementStatesConfig: ReplacementListState[] = [];
    replacementStateConfigToManage: ReplacementListState | null = null;
    processConfigurationTypes = ProcessConfigurationTypes;
    constants = Constants;
    tabActiveLanguage: string = '';
    isLoading: boolean = false;
    newReplacementStateConfig: ReplacementListState | null = null;
    submittedNew: boolean = false;
    invalidForm: any;

    $refs!: {
        processConfigReplacementStatesCreateForm: HTMLFormElement
    };

    get replacementStatesConfigOrdered() {
        return _.orderBy(this.replacementStatesConfig, ['isEditable', replacementState => _.deburr(replacementState.name[this.currentLanguage]).toLowerCase()]);
    }

    get notCurrentLanguage() {
        return this.availableLanguages.find(language => language !== this.currentLanguage);
    }
    
    getReplacementStatesSelectOptions() {
        return this.replacementStatesConfig.filter(replacementState => !replacementState.isEditable)
            .map(replacementState => ({ id: replacementState.value, title: replacementState.name[this.currentLanguage], name: replacementState.name[this.currentLanguage] }));
    }    

    createNewReplacementStateConfig() {
        this.newReplacementStateConfig = new ReplacementListState({});
        this.resetActiveLanguage();
        this.cancelEditedReplacementStateConfig();
    }

    editReplacementStateConfig(editReplacementStateConfig: ReplacementListState) {
        this.resetActiveLanguage();
        this.replacementStateConfigToManage = _.cloneDeep(editReplacementStateConfig);
        this.removeNewReplacementStateConfig();
    }

    cancelEditedReplacementStateConfig() {
        this.replacementStateConfigToManage = null;
        this.$emit('update:submittedEdit', false);
    }

    removeNewReplacementStateConfig() {
        this.newReplacementStateConfig = null;
        this.submittedNew = false;
    }

    async saveEditedReplacementStateConfig() {
        this.$emit('update:submittedEdit', true);
        if (this.replacementStateConfigToManage === null || (this.replacementStateConfigToManage && !this.replacementStateConfigToManage.name.allHasValue(this.availableLanguages))) {
            return;
        }

        this.isLoading = true;
        try {
            this.replacementStateConfigToManage.setBehaviours(this.getSystemBehaviours(this.replacementStateConfigToManage));

            await ReplacementService.updateReplacementListState(this.replacementStateConfigToManage);
            this.handlesUpdateOrCreateReplacementStateConfigOnSuccess();
        } catch (error) {
            console.error(error);
            this.handlesUpdateOrCreateReplacementStateConfigOnFailure();
        } finally {
            this.isLoading = false;
        }
    }

    async saveNewReplacementStateConfig() {
        this.submittedNew = true;
        this.invalidForm = this.$refs.processConfigReplacementStatesCreateForm.querySelectorAll(':invalid').length > 0 || this.$refs.processConfigReplacementStatesCreateForm.querySelectorAll('.error').length > 0;

        if (this.invalidForm || this.newReplacementStateConfig === null) {
            return;
        }

        this.isLoading = true;
        try {
            this.newReplacementStateConfig.setBehaviours(this.getSystemBehaviours(this.newReplacementStateConfig));

            await ReplacementService.createReplacementListState(this.newReplacementStateConfig);
            this.handlesUpdateOrCreateReplacementStateConfigOnSuccess();
        } catch (error) {
            console.error(error);
            this.handlesUpdateOrCreateReplacementStateConfigOnFailure();
        } finally {
            this.isLoading = false;
        }
    }

    toggleEditingReplacementStateConfigVisibility() {
        if (this.replacementStateConfigToManage === null || !this.replacementStateConfigToManage.isEditable) {
            return;
        }

        this.replacementStateConfigToManage.isVisible = !this.replacementStateConfigToManage.isVisible;
    }

    toggleCreatingReplacementStateConfigVisibility() {
        if (this.newReplacementStateConfig === null) {
            return;
        }

        this.newReplacementStateConfig.isVisible = !this.newReplacementStateConfig.isVisible;
    }
    
    mounted() {
        this.getReplacementStatesConfig();
    }

    private async getReplacementStatesConfig() {
        this.isLoading = true;
        try {
            const response = await ReplacementService.getReplacementListStates();
            this.replacementStatesConfig = response.map(replacementState => new ReplacementListState(replacementState));
        } catch (error) {
            console.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    private getSystemBehaviours(state: ReplacementListState) {
        const replacementState = this.replacementStatesConfig.find(rs => !rs.isEditable && rs.value === state.value);

        if (!replacementState) {
            throw new Error(`Replacement state not found for value: ${state.value}`);
        }

        const behaviours = replacementState.getBehaviours();
        return behaviours;
    }

    private handlesUpdateOrCreateReplacementStateConfigOnSuccess() {
        ToasterService.showSuccess(i18n.t('lang.toaster.saveSuccess') as string);
        this.replacementStateConfigToManage = null;
        this.newReplacementStateConfig = null;
        this.submittedNew = false;
        this.$emit('update:submittedEdit', false);
        this.getReplacementStatesConfig();
    }

    private handlesUpdateOrCreateReplacementStateConfigOnFailure() {
        ToasterService.showError(i18n.t('lang.toaster.saveError') as string);
        this.replacementStateConfigToManage = null;
        this.newReplacementStateConfig = null;
        this.submittedNew = false;
        this.$emit('update:submittedEdit', false);
        this.getReplacementStatesConfig();
    }

    private resetActiveLanguage() {
        this.tabActiveLanguage = this.availableLanguages[0];
    }

}
